import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Plus, Play, Pause, Trash2, Users, Send, Eye, MousePointerClick,
  MessageSquare, AlertTriangle, RefreshCw, Loader2, ExternalLink, X,
  Check, ChevronRight, Link2
} from 'lucide-react';
import api, {
  EmailCampaign, EmailCampaignDetail, CampaignEnrollment,
  EmailCampaignStats, InstantlyCampaign
} from '../services/api';

const CAMPAIGN_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'paused', label: 'Paused', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
];

const ENROLLMENT_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' },
  { value: 'active', label: 'Active', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'replied', label: 'Replied', color: 'bg-purple-100 text-purple-800' },
  { value: 'bounced', label: 'Bounced', color: 'bg-red-100 text-red-800' },
  { value: 'unsubscribed', label: 'Unsubscribed', color: 'bg-orange-100 text-orange-800' },
];

const getStatusConfig = (status: string, list: typeof CAMPAIGN_STATUSES) => {
  return list.find(s => s.value === status) || list[0];
};

const EmailCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [stats, setStats] = useState<EmailCampaignStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selected campaign for detail view
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaignDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Link campaign modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<InstantlyCampaign[]>([]);
  const [loadingInstantly, setLoadingInstantly] = useState(false);
  const [selectedInstantlyCampaign, setSelectedInstantlyCampaign] = useState<string>('');
  const [newCampaignName, setNewCampaignName] = useState('');
  const [linking, setLinking] = useState(false);

  // Syncing state
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [campaignsData, statsData] = await Promise.all([
        api.getEmailCampaigns(),
        api.getEmailCampaignStats(),
      ]);
      setCampaigns(campaignsData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectCampaign = async (campaign: EmailCampaign) => {
    setLoadingDetail(true);
    try {
      const detail = await api.getEmailCampaign(campaign.id);
      setSelectedCampaign(detail);
    } catch (err) {
      console.error('Failed to load campaign detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleActivate = async (campaign: EmailCampaign) => {
    try {
      await api.activateEmailCampaign(campaign.id);
      fetchData();
      if (selectedCampaign?.id === campaign.id) {
        handleSelectCampaign(campaign);
      }
    } catch (err) {
      console.error('Failed to activate campaign:', err);
    }
  };

  const handlePause = async (campaign: EmailCampaign) => {
    try {
      await api.pauseEmailCampaign(campaign.id);
      fetchData();
      if (selectedCampaign?.id === campaign.id) {
        handleSelectCampaign(campaign);
      }
    } catch (err) {
      console.error('Failed to pause campaign:', err);
    }
  };

  const handleDelete = async (campaign: EmailCampaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"? This will not delete leads from Instantly.`)) {
      return;
    }
    try {
      await api.deleteEmailCampaign(campaign.id);
      fetchData();
      if (selectedCampaign?.id === campaign.id) {
        setSelectedCampaign(null);
      }
    } catch (err) {
      console.error('Failed to delete campaign:', err);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.syncInstantlyCampaigns();
      alert(`Synced ${result.synced} campaigns (${result.created} new)`);
      fetchData();
    } catch (err) {
      console.error('Failed to sync:', err);
      alert('Failed to sync campaigns from Instantly');
    } finally {
      setSyncing(false);
    }
  };

  const openLinkModal = async () => {
    setShowLinkModal(true);
    setLoadingInstantly(true);
    try {
      const campaigns = await api.listInstantlyCampaigns();
      setInstantlyCampaigns(campaigns);
    } catch (err) {
      console.error('Failed to load Instantly campaigns:', err);
    } finally {
      setLoadingInstantly(false);
    }
  };

  const handleLinkCampaign = async () => {
    if (!selectedInstantlyCampaign) return;

    const instantlyCamp = instantlyCampaigns.find(c => c.id === selectedInstantlyCampaign);
    const name = newCampaignName || instantlyCamp?.name || 'Unnamed Campaign';

    setLinking(true);
    try {
      await api.createEmailCampaign({
        name,
        instantly_campaign_id: selectedInstantlyCampaign,
      });
      setShowLinkModal(false);
      setSelectedInstantlyCampaign('');
      setNewCampaignName('');
      fetchData();
    } catch (err) {
      console.error('Failed to link campaign:', err);
      alert('Failed to link campaign');
    } finally {
      setLinking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Cold Email Campaigns
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage Instantly.ai campaigns and track engagement
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync from Instantly
          </button>
          <button
            onClick={openLinkModal}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <Plus className="w-4 h-4" />
            Link Campaign
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Mail className="w-4 h-4" />
              Campaigns
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.total_campaigns}
            </div>
            <div className="text-xs text-green-600 mt-1">
              {stats.active_campaigns} active
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Users className="w-4 h-4" />
              Total Leads
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.total_leads.toLocaleString()}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Send className="w-4 h-4" />
              Sent
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.total_sent.toLocaleString()}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <Eye className="w-4 h-4" />
              Opened
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.total_opened.toLocaleString()}
            </div>
            <div className="text-xs text-blue-600 mt-1">
              {stats.open_rate}% rate
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <MousePointerClick className="w-4 h-4" />
              Clicked
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {stats.total_clicked.toLocaleString()}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <MessageSquare className="w-4 h-4" />
              Replied
            </div>
            <div className="text-2xl font-bold text-purple-600 mt-1">
              {stats.total_replied.toLocaleString()}
            </div>
            <div className="text-xs text-purple-600 mt-1">
              {stats.reply_rate}% rate
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex gap-6">
        {/* Campaign list */}
        <div className={`${selectedCampaign ? 'w-1/2' : 'w-full'} space-y-3`}>
          {campaigns.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
              <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No campaigns yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Link an Instantly.ai campaign to start tracking engagement
              </p>
              <button
                onClick={openLinkModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Link Campaign
              </button>
            </div>
          ) : (
            campaigns.map(campaign => {
              const statusConfig = getStatusConfig(campaign.status, CAMPAIGN_STATUSES);
              const isSelected = selectedCampaign?.id === campaign.id;

              return (
                <div
                  key={campaign.id}
                  onClick={() => handleSelectCampaign(campaign)}
                  className={`bg-white dark:bg-gray-800 rounded-lg border p-4 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary-500 ring-1 ring-primary-500'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {campaign.name}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {campaign.total_leads} leads
                        </span>
                        <span className="flex items-center gap-1">
                          <Send className="w-4 h-4" />
                          {campaign.emails_sent} sent
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {campaign.open_rate}% open
                        </span>
                        <span className="flex items-center gap-1 text-purple-600">
                          <MessageSquare className="w-4 h-4" />
                          {campaign.emails_replied} replies
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      {campaign.status === 'active' ? (
                        <button
                          onClick={() => handlePause(campaign)}
                          className="p-2 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded"
                          title="Pause"
                        >
                          <Pause className="w-4 h-4" />
                        </button>
                      ) : campaign.status !== 'completed' && (
                        <button
                          onClick={() => handleActivate(campaign)}
                          className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                          title="Activate"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(campaign)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Detail panel */}
        {selectedCampaign && (
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {selectedCampaign.name}
                  </h2>
                  <button
                    onClick={() => setSelectedCampaign(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Campaign stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{selectedCampaign.open_rate}%</div>
                    <div className="text-xs text-gray-500">Open Rate</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{selectedCampaign.reply_rate}%</div>
                    <div className="text-xs text-gray-500">Reply Rate</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{selectedCampaign.bounce_rate}%</div>
                    <div className="text-xs text-gray-500">Bounce Rate</div>
                  </div>
                </div>

                {/* Enrollments */}
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                    Enrollments ({selectedCampaign.enrollments.length})
                  </h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedCampaign.enrollments.length === 0 ? (
                      <p className="text-gray-500 text-sm py-4 text-center">
                        No contacts enrolled yet
                      </p>
                    ) : (
                      selectedCampaign.enrollments.map(enrollment => {
                        const statusConfig = getStatusConfig(enrollment.status, ENROLLMENT_STATUSES);
                        return (
                          <div
                            key={enrollment.id}
                            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                          >
                            <div>
                              <div className="font-medium text-sm text-gray-900 dark:text-white">
                                {enrollment.contact_name || enrollment.contact_email}
                              </div>
                              <div className="text-xs text-gray-500">
                                {enrollment.company_name}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-xs text-gray-500">
                                {enrollment.emails_sent} sent, {enrollment.emails_opened} opened
                              </div>
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.color}`}>
                                {statusConfig.label}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Instantly link */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <a
                    href={`https://app.instantly.ai/campaign/${selectedCampaign.instantly_campaign_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View in Instantly
                  </a>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Link Campaign Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Link Instantly Campaign
              </h2>
              <button
                onClick={() => setShowLinkModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loadingInstantly ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Instantly Campaign
                  </label>
                  <select
                    value={selectedInstantlyCampaign}
                    onChange={(e) => {
                      setSelectedInstantlyCampaign(e.target.value);
                      const camp = instantlyCampaigns.find(c => c.id === e.target.value);
                      if (camp) setNewCampaignName(camp.name);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  >
                    <option value="">Choose a campaign...</option>
                    {instantlyCampaigns.filter(c => !c.is_linked).map(camp => (
                      <option key={camp.id} value={camp.id}>
                        {camp.name} ({camp.status})
                      </option>
                    ))}
                  </select>
                  {instantlyCampaigns.filter(c => !c.is_linked).length === 0 && (
                    <p className="text-sm text-gray-500 mt-2">
                      All campaigns are already linked. Create a new campaign in Instantly first.
                    </p>
                  )}
                </div>

                {selectedInstantlyCampaign && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Display Name (optional)
                    </label>
                    <input
                      type="text"
                      value={newCampaignName}
                      onChange={(e) => setNewCampaignName(e.target.value)}
                      placeholder="Campaign name in Digimint"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLinkCampaign}
                    disabled={!selectedInstantlyCampaign || linking}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {linking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4" />
                    )}
                    Link Campaign
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaignsPage;
